import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import AssetType from "../../models/asset-type";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { BsFillPlusCircleFill } from "react-icons/bs";
import AssetTypeUpdateModal from "./AssetTypeUpdateModal";
import AssetTypeDeleteModal from "./AssetTypeDeleteModal";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  ASSETS_CREATE,
  ASSETS_DESTROY,
  ASSETS_UPDATE,
} from "../../auth/permissions";

const ITEMS_PER_PAGE = 10;

const AssetTypeList = () => {
  const fields = [
    {
      key: "name",
      _classes: ["text-center"],
      label: "Nombre",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [search, setSearch] = useState<string>("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetType, setAssetType] = useState<AssetType | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();
  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchAssetTypes = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    urlParams.set("company_id", companyId);
    const assettypesStatus = await getList<AssetType>(
      "/asset_types/",
      limit,
      offset,
      urlParams
    );
    if (assettypesStatus.status === SUCCESS) {
      if (assettypesStatus.data !== undefined) {
        const count = assettypesStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setAssetTypes(assettypesStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = assettypesStatus.detail
        ? assettypesStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowEditModal(true);
  };

  const onUpdateClick = (assetType: AssetType) => {
    setAssetType(assetType);
    setShowEditModal(true);
  };

  const onDeleteClick = (assetType: AssetType) => {
    setAssetType(assetType);
    setShowDeleteModal(true);
  };

  const onAssetTypeEditCancel = () => {
    setAssetType(undefined);
    setShowEditModal(false);
  };

  const onAssetTypeEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setAssetTypes([]);
    setLoading(true);
    fetchAssetTypes();
  };

  const onAssetTypeDeleteCancel = () => {
    setAssetType(undefined);
    setShowDeleteModal(false);
  };

  const onAssetTypeDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setAssetTypes([]);
    setLoading(true);
    fetchAssetTypes();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchAssetTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("assetTypes")) {
        return;
      }
      fetchAssetTypes();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Tipos de Activos</h3>
              </div>
              <PermissionRequiredComponent permissionName={ASSETS_CREATE}>
                <div className="p-2 ">
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Agregar Nuevo
                  </CButton>
                </div>
              </PermissionRequiredComponent>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={assetTypes}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    actions: (item: AssetType) => {
                      const editButton = (
                        <CButton
                          className="text-white"
                          color="warning"
                          onClick={() => {
                            onUpdateClick(item);
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </CButton>
                      );
                      const deleteButton = (
                        <CButton
                          className="text-white"
                          color="danger"
                          onClick={() => {
                            onDeleteClick(item);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </CButton>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            <PermissionRequiredComponent
                              permissionName={ASSETS_UPDATE}
                            >
                              {editButton}
                            </PermissionRequiredComponent>
                            <PermissionRequiredComponent
                              permissionName={ASSETS_DESTROY}
                            >
                              {deleteButton}
                            </PermissionRequiredComponent>
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <AssetTypeUpdateModal
        show={showEditModal}
        onCancel={onAssetTypeEditCancel}
        onSuccess={onAssetTypeEditSuccess}
        assetType={assetType}
      />
      <AssetTypeDeleteModal
        show={showDeleteModal}
        onCancel={onAssetTypeDeleteCancel}
        onSuccess={onAssetTypeDeleteSuccess}
        assetType={assetType}
      />
    </>
  );
};

export default AssetTypeList;
